import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getTaxReport401, getTaxReport403, getTaxSummary, getTaxPeriods } from '../services/api';

interface SalesTaxReport401 {
  reportPeriod: string;
  taxableAmount: number;
  taxExemptAmount: number;
  zeroRatedAmount: number;
  outputTax: number;
  totalSalesAmount: number;
  previousPeriodBalance: number;
  payableTax: number;
  organizationInfo: {
    name: string;
    taxId: string;
    address: string;
  };
  generatedAt: string;
  details: TaxReportDetail[];
}

interface PurchaseTaxReport403 {
  reportPeriod: string;
  inputTaxAmount: number;
  nonDeductibleAmount: number;
  deductibleAmount: number;
  specialDeductions: number;
  totalDeductible: number;
  organizationInfo: {
    name: string;
    taxId: string;
    address: string;
  };
  generatedAt: string;
  details: TaxReportDetail[];
}

interface TaxReportDetail {
  accountCode: string;
  accountName: string;
  amount: number;
  taxRate: number;
  taxAmount: number;
  description: string;
  invoiceNumbers?: string[];
}

interface TaxCalculationSummary {
  period: string;
  totalOutputTax: number;
  totalInputTax: number;
  netTaxPayable: number;
  carryForwardBalance: number;
  isRefundable: boolean;
}

interface TaxReportData {
  report401: SalesTaxReport401 | null;
  report403: PurchaseTaxReport403 | null;
  taxSummary: TaxCalculationSummary | null;
}

interface TaxReportState {
  availablePeriods: string[];
  selectedPeriod: string;
  periodDataCache: Record<string, { data: TaxReportData; fetchTime: number }>;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchTaxPeriods: () => Promise<void>;
  setSelectedPeriod: (period: string) => void;
  fetchTaxReportData: (period: string, forceRefresh?: boolean) => Promise<void>;
  clearPeriodCache: (period?: string) => void;
  clearAllCache: () => void;
}

const fetchTaxDataForPeriod = async (period: string): Promise<TaxReportData> => {
  const [res401, res403, resSummary] = await Promise.all([
    getTaxReport401(period),
    getTaxReport403(period),
    getTaxSummary(period)
  ]);

  return {
    report401: res401.data,
    report403: res403.data,
    taxSummary: resSummary.data
  };
};

export const useTaxReportStore = create<TaxReportState>()(
  persist(
    (set, get) => ({
      availablePeriods: [],
      selectedPeriod: '',
      periodDataCache: {},
      isLoading: false,
      error: null,

      fetchTaxPeriods: async () => {
        try {
          const response = await getTaxPeriods();
          const periods = response.data.periods || [];
          set({
            availablePeriods: periods,
            selectedPeriod: periods.length > 0 ? periods[0] : ''
          });
        } catch (err: any) {
          console.error('載入申報期間失敗:', err);
          set({ error: '載入申報期間失敗' });
        }
      },

      setSelectedPeriod: (period: string) => {
        set({ selectedPeriod: period });
      },

      fetchTaxReportData: async (period: string, forceRefresh = false) => {
        const state = get();
        const cacheKey = period;

        // 檢查快取是否有效（非強制刷新時）
        if (!forceRefresh && state.periodDataCache[cacheKey]) {
          const cached = state.periodDataCache[cacheKey];
          // 快取永久有效，直到手動清除
          set({
            isLoading: false,
            error: null
          });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          const taxData = await fetchTaxDataForPeriod(period);
          const fetchTime = Date.now();

          set({
            periodDataCache: {
              ...state.periodDataCache,
              [cacheKey]: { data: taxData, fetchTime }
            },
            isLoading: false,
            error: null
          });
        } catch (err: any) {
          console.error('載入稅務資料失敗:', err);
          set({
            error: err.response?.data?.message || '載入稅務資料失敗',
            isLoading: false
          });
        }
      },

      clearPeriodCache: (period?: string) => {
        const state = get();
        if (period) {
          const newCache = { ...state.periodDataCache };
          delete newCache[period];
          set({ periodDataCache: newCache });
        } else {
          set({ periodDataCache: {} });
        }
      },

      clearAllCache: () => {
        set({
          periodDataCache: {},
          error: null
        });
      },
    }),
    {
      name: 'tax-report-storage',
      partialize: (state) => ({
        availablePeriods: state.availablePeriods,
        selectedPeriod: state.selectedPeriod,
        periodDataCache: state.periodDataCache,
      }),
    }
  )
);
